/**
 * Licensed Materials - Property of IBM
 * IBM Cognos Products: ba-ui-common (C) Copyright IBM Corp. 2019, 2020
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */

import PropTypes from 'prop-types';

import React, { Component } from 'react';
import stringResource from 'ba-filter-nls';
import { FlexLayout, FlexItem, TextInput, Select } from '@waca/ca-ui-toolkit';
import ConditionTypeOptions from './ConditionTypeOptions';

export default class ConditionSelector extends Component<any, any> {
	// Define properties and types
	static propTypes = {
		position: PropTypes.number.isRequired,
		type: PropTypes.oneOf([
			'equals',
			'contains',
			'startsWith',
			'endsWith',
			'doesNotEqual',
			'doesNotContain',
			'doesNotBeginWith',
			'doesNotEndWith',
		]),
		invert: PropTypes.bool,
		value: PropTypes.string,
		onChange: PropTypes.func.isRequired,
		'data-tid': PropTypes.string,
	};

	constructor(props) {
		super(props);
		this._options = new ConditionTypeOptions();
	}

	_options: ConditionTypeOptions;

	// Event handlers
	_onConditionTypeChanged = e => {
		const option = this._options.list.find(item => item.value === e);
		const result = this._buildPredicate(option.operation, this.props.value, option.invert);

		this.props.onChange(result);
	};

	_onConditionValueChanged = e => {
		let option = this._options.list.find(
			item => item.operation === this.props.type && item.invert === this.props.invert
		);

		if (!option) {
			option = this._options.defaultOption;
		}
		const result = this._buildPredicate(option.operation, e, option.invert);

		this.props.onChange(result);
	};

	// Build the JSON structure we will return. Fill in default values
	// if we do not have them
	_buildPredicate = (type, value, invert) => {
		const { defaultOption } = this._options;
		const condition = {
			position: this.props.position,
			predicate: {
				conditionType: !type ? defaultOption.value : type,
				value: !value ? '' : value,
				invert: !invert ? defaultOption.invert : invert,
			},
		};

		return condition;
	};

	render() {
		const { value, type, invert } = this.props;
		const options = this._options.list;
		const selectedLabel = !type
			? this._options.defaultOption.value
			: options.find(item => item.operation === type && item.invert === invert).value;

		return (
			<FlexLayout direction="column" data-tid={this.props['data-tid']}>
				<FlexItem margin={[3, 0, 0, 0]}>
					<Select
						data-tid="conditionDropDown"
						onChange={this._onConditionTypeChanged}
						size="small"
						truncated={false}
						customWidth="100%"
						selected={selectedLabel}
						options={options}
						domNodeToAttachTo={false}
					/>
				</FlexItem>
				<FlexItem margin={[1, 0, 0, 0]}>
					<TextInput
						fullWidth
						placeholder={stringResource.get('enterValue')}
						value={value}
						onChange={this._onConditionValueChanged}
					/>
				</FlexItem>
			</FlexLayout>
		);
	}
}
